import { Alert, AutocompleteRenderInputParams, Box, Button, CircularProgress, Divider, IconButton, Stack, TextField, Typography, Tabs, Tab } from "@mui/material";
import { usePushbackLoans } from "../../hooks/pushbackDashboard/usePushbackLoans";
import { usePushbackOrderDetails } from "../../hooks/pushbackDashboard/usePushbackOrderDetails";
import { usePushbackDetail } from "../../hooks/pushbackDashboard/usePushbackDetail";
import { useState, useCallback, useRef, useEffect } from "react";
import LoansListDateFilter from "../../components/pushbackDashboard/loanListDateFilter";
import { orderDetailsColumns, pushBackDetailColumns } from "./pushbackData";
import { usePushbackClientLogs } from "../../hooks/pushbackDashboard/usePushbackClientLogs";
import { clientLogs } from "../clientData";
import PushbackReportingDialog from "./PushbackReportingDialog";
import { usePushbackReport } from "../../hooks/pushbackDashboard/usePushbackReport";
import { IPushbackEntry } from "../../models/configuration/pushback/IPushbackLoans";
import { Refresh, Inbox as InboxIcon, Visibility as VisibilityIcon, History as HistoryIcon } from "@mui/icons-material";
import { DtPageHeader } from "../../components/ui/header";
import { DtAutocomplete } from "../../components/ui/autocomplete";
import { DtDataGrid } from "../../components/ui/dataGrid";
import { EmptyState } from "../../components/ui/emptyState";
import colorTheme from "../../assets/colorTheme";
import DtAlert from "../../components/ui/alert/DtAlert";

const PushbackDashboard: React.FC = () => {

    // State
    const [selectedLoanDate, setSelectedLoanDate] = useState<string>('');
    const [selectedLoan, setSelectedLoan] = useState<string>('');
    const [orderDetailsOrderId, setOrderDetailsOrderId] = useState<string>('');
    const [reportingDialogOpen, setReportingDialogOpen] = useState(false);
    const [reportOrderId, setReportOrderId] = useState<string>('');
    const [reportEntryId, setReportEntryId] = useState<string>('');
    const [tabIndex, setTabIndex] = useState(0);

    // Hooks
    const { data: loansData, error: loansError, loading: loansIsLoading, refresh: refreshLoans } = usePushbackLoans(selectedLoanDate);
    const { data: orderDetailsData, error: orderDetailsError, loading: orderDetailsIsLoading } = usePushbackOrderDetails(selectedLoan);
    const { data: detailData, error: detailError, loading: detailIsLoading, refresh: refreshDetail } = usePushbackDetail(orderDetailsOrderId);
    const { data: logData, error: logError, loading: logIsLoading } = usePushbackClientLogs(selectedLoan);
    const { data: reportData, loading: reportIsLoading, refresh: refreshReport } = usePushbackReport(reportOrderId, reportEntryId);

    // Handlers
    const handleLoanChange = useCallback((loanNumber: string) => {
        setSelectedLoan(loanNumber);
        setOrderDetailsOrderId('');
    }, []);

    const handleLoansDateChange = useCallback((date: string) => {
        setSelectedLoanDate(date);
        setSelectedLoan('');
        setOrderDetailsOrderId('');
    }, []);

    const handleRefreshLoans = useCallback(() => {
        // Clear selected loan and order details when refreshing loans
        setSelectedLoan('');
        setOrderDetailsOrderId('');
        // Directly trigger the refresh without changing the date
        refreshLoans();
    }, [refreshLoans]);

    const handleViewReport = useCallback((item: IPushbackEntry) => {
        setReportOrderId(item.OrderId);
        setReportEntryId(item.EntryId);
        setReportingDialogOpen(true);
    }, []);

    const reportingDialogClose = useCallback(() => {
        setReportingDialogOpen(false);
    }, []);

    // Clear order details and client logs when loans start loading
    useEffect(() => {
        if (loansIsLoading) {
            setSelectedLoan('');
            setOrderDetailsOrderId('');
        }
    }, [loansIsLoading]);

    // Auto-select first loan when loan list refresh completes
    useEffect(() => {
        if (loansData?.LoanNumbers && loansData.LoanNumbers.length > 0 && !loansIsLoading) {
            const firstLoan = loansData.LoanNumbers[0];
            setSelectedLoan(firstLoan);
        }
    }, [loansData, loansIsLoading]);

    // Auto-select first order when order details load for the selected loan
    useEffect(() => {
        if (orderDetailsData?.OrderDetails && orderDetailsData.OrderDetails.length > 0 && selectedLoan && !orderDetailsIsLoading) {
            setOrderDetailsOrderId(orderDetailsData.OrderDetails[0].OrderId);
        }
    }, [orderDetailsData, orderDetailsIsLoading, selectedLoan]);

    // Add error state for loans, order details, and logs
    const hasAnyError = !!(loansError || orderDetailsError || logError);

    return (
        <Stack sx={{display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DtPageHeader>Pushback Dashboard</DtPageHeader>

            <Stack sx={{ px: '0.5rem', gap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LoansListDateFilter
                            DefaultDate={new Date().toLocaleDateString('en-US')}
                            OutputDate={handleLoansDateChange}
                        />
                    </Box>
                    <Box>
                        <DtAutocomplete
                            id="select-loan-autocomplete"
                            options={loansData?.LoanNumbers || []}
                            value={selectedLoan}
                            onChange={(_, value) => handleLoanChange(value)}
                            getOptionLabel={(option) => option?.toString() ?? ""}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Loan" size="small" variant="outlined" />
                            )}
                            loading={loansIsLoading}
                            disabled={loansIsLoading || !loansData?.LoanNumbers?.length}
                            sx={{ minWidth: 240 }}
                            loadingText="Loading loans..."
                        />
                    </Box>
                    <Button
                        onClick={handleRefreshLoans}
                        color="secondary"
                        title="Refresh loans"
                        disabled={loansIsLoading}
                        sx={{ minWidth: 140 }}
                    >
                        {loansIsLoading ? (
                            <>
                                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                Refreshing Loan List...
                            </>
                        ) : (
                            <>
                                <Refresh sx={{ mr: 1 }} />
                                Refresh Loan List
                            </>
                        )}
                    </Button>
                </Stack>

                {loansData?.LoanNumbers?.length === 0 && !loansIsLoading && (
                    <DtAlert severity="warning">No Loans found for selected date. Please change date and try again.</DtAlert>
                )}

                <Divider sx={{ mt: 0.3 }} />

                {/* Error Alert */}
                {hasAnyError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {loansError && <span>Failed to load/refresh loans. </span>}
                        {orderDetailsError && <span>Failed to load order details. </span>}
                        {logError && <span>Failed to load client logs. </span>}
                        Please try refreshing or check your connection.
                    </Alert>
                )}

                {/* Use default Material UI Tabs */}
                <Tabs
                    value={tabIndex}
                    onChange={(_, newValue) => setTabIndex(newValue)}
                    sx={{ minHeight: 24 }}
                >
                    <Tab
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 1, }}>
                                    Order Details
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    color={
                                        tabIndex === 0
                                            ? "primary.main"
                                            : "text.secondary"
                                    }
                                >
                                    {orderDetailsIsLoading ? "" : `(${orderDetailsData?.OrderDetails?.length ?? 0})`}
                                </Typography>
                            </Box>
                        }
                        sx={{ minHeight: 36 }}
                    />
                    <Tab
                        label={
                            <Box sx={{ display: "flex", alignItems: "center" ,  mb: 1}}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 1 }}>
                                    Client Logs
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    color={
                                        tabIndex === 1
                                            ? "primary.main"
                                            : "text.secondary"
                                    }
                                >
                                    {logIsLoading ? "" : `(${logData?.ClientLogEntries?.length ?? 0})`}
                                </Typography>
                            </Box>
                        }
                        sx={{ minHeight: 36 }}
                    />
                </Tabs>

                {/* Tab Panels */}
                {tabIndex === 0 && (
                    <Box>
                        {orderDetailsIsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                                <CircularProgress />
                            </Box>
                        ) : orderDetailsData?.OrderDetails?.length! > 0 ? (
                            <DtDataGrid
                                data={orderDetailsData?.OrderDetails ? [...orderDetailsData.OrderDetails].sort((a, b) => new Date(b.CreatedTimestamp).getTime() - new Date(a.CreatedTimestamp).getTime()) : []}
                                columns={orderDetailsColumns()}
                                tableName="orderDetails"
                                paged
                                responsivePageSize
                                responsivePageConfig={{heightRatio: 0.6}}
                                expanderConfig={{
                                    expanderTooltip: (expanded, row) => expanded ? "Hide Pushback details" : "Show Pushback details",
                                    onRowExpand: (row, rowIndex, expanded) => {
                                        setOrderDetailsOrderId(expanded ? row.OrderId : "");
                                    },
                                    renderExpandedRow: (row) =>
                                        orderDetailsOrderId === row.OrderId ? (
                                            detailIsLoading ? (
                                                <Box sx={{ display: "flex", justifyContent: "start", alignItems: "center", minHeight: 25 }}>
                                                    <CircularProgress size={25}/>
                                                </Box>
                                            ) : detailData?.PushbackEntries?.length ? (
                                                <>
                                                        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 2, fontWeight: 500, mb: 1, color: "text.secondary" }}>
                                                            Pushback Details
                                                            <Button
                                                                size="small"
                                                                startIcon={<Refresh />}
                                                                onClick={refreshDetail}
                                                            >
                                                                Refresh
                                                            </Button>
                                                        </Typography>
                                                        <Box sx={{ background: colorTheme.palette.background.paper}}>
                                                            <DtDataGrid
                                                                data={[...detailData.PushbackEntries].sort(
                                                                    (a, b) =>
                                                                        new Date(b.CreatedTimestamp).getTime() -
                                                                        new Date(a.CreatedTimestamp).getTime()
                                                                )}
                                                                paged
                                                                columns={pushBackDetailColumns()}
                                                                tableName="pushBackDetails"
                                                                actionsConfig={{
                                                                    mode: 'dropdown',
                                                                    actions: [{
                                                                        type: 'view',
                                                                        label: "View Report",
                                                                        onClick: (row) => handleViewReport(row)
                                                                    }]
                                                                }}
                                                            />
                                                        </Box>
                                                </>
                                            ) : (
                                                <Typography sx={{ color: "text.secondary", fontWeight: 500}}>
                                                    No pushback details available.
                                                </Typography>
                                            )
                                        ) : null
                                }}
                            />
                        ) : (
                            <EmptyState
                                icon={<InboxIcon sx={{ fontSize: 48, color: "primary.main" }} />}
                                header="No order details available"
                                subheader="Try changing your filters selection."
                                actionLabel="Refresh List"
                                minHeight={250}
                            />
                        )}
                    </Box>
                )}

                {tabIndex === 1 && (
                    <Box>
                        {logIsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                                <CircularProgress />
                            </Box>
                        ) : (logData?.ClientLogEntries?.length ?? 0) > 0 ? (
                            <DtDataGrid
                                data={logData?.ClientLogEntries || []}
                                columns={clientLogs()}
                                tableName="logs"
                            />
                        ) : (
                            <EmptyState
                                icon={<HistoryIcon sx={{ fontSize: 48, color: "primary.main" }} />}
                                header="No client logs available"
                                subheader="Try changing your filters selection."
                                minHeight={250}
                            />
                        )}
                    </Box>
                )}

            </Stack>

            {/* Modal for Pushback Details */}
            <PushbackReportingDialog
                open={reportingDialogOpen}
                closeFxtn={reportingDialogClose}
                reportData={reportData}
                loading={reportIsLoading}
                onRefresh={refreshReport}
            />
        </Stack>
    );
};

export default PushbackDashboard;